/**
 * ApiKeysIntegrations — B2C mirror of B2B IntegrationsPage.
 *
 * Layout: grouped Section cards, each item as a row with
 *   icon · name · status pill · description · Configure/Manage CTA
 *   ↳ inline-expand form on click (no modal needed for most)
 *
 * Sections
 *   1. Personal API Keys   — generate / revoke personal access tokens
 *   2. AI & Intelligence   — Ollama (local) · Groq (cloud)
 *   3. Job Channels        — Peerlist (built-in) · LinkedIn · Naukri · Indeed · Internshala
 *   4. Roadmap             — planned connectors (dashed cards)
 */
import {
  AlertTriangle,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Globe,
  Info,
  KeyRound,
  Plus,
  Save,
  Shield,
  ShieldAlert,
  Trash2,
  X as XIcon,
  Zap,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import LoadingSpinner from '../../components/LoadingSpinner';
import { supabase } from '../../lib/supabase';
import { loadExternalCreds } from '../dashboard/components/DeveloperSettings';
import type { ExternalCred } from '../dashboard/components/DeveloperSettings';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ApiKeyRow {
  id: string;
  key_prefix: string;
  label: string | null;
  scopes: string[] | null;
  rate_limit_rpm: number | null;
  created_at: string;
  last_used_at: string | null;
}

interface GenerateResponse {
  id: string;
  raw_key: string;
  key_prefix: string;
  label: string | null;
  scopes: string[];
  rate_limit_rpm: number;
  created_at: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const sec = Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 1000));
  if (sec < 60) return 'Just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86_400 * 30) return `${Math.floor(sec / 86_400)}d ago`;
  return new Date(Date.parse(iso)).toLocaleDateString();
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(Date.parse(iso)).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

const EXTERNAL_STORAGE_KEY = 'ggj_external_search_creds';

function saveExternalCreds(creds: ExternalCred[]) {
  localStorage.setItem(EXTERNAL_STORAGE_KEY, JSON.stringify(creds));
}

// ─────────────────────────────────────────────────────────────────────────────
// Section wrapper — same visual as B2B ggj-param-group
// ─────────────────────────────────────────────────────────────────────────────

const IntSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <div className="ds-akk-section">
    <div className="ds-akk-section-header">
      {icon}
      {title}
    </div>
    <div className="ds-akk-section-body">{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Status pill — same as B2B
// ─────────────────────────────────────────────────────────────────────────────

type PillTone = 'ok' | 'off' | 'warn' | 'info';

const StatusPill: React.FC<{ label: string; tone: PillTone }> = ({ label, tone }) => (
  <span className={`ds-akk-pill ds-akk-pill--${tone}`}>
    {tone === 'ok' && <CheckCircle className="w-3 h-3" aria-hidden />}
    {tone === 'warn' && <AlertTriangle className="w-3 h-3" aria-hidden />}
    {label}
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Integration row — same visual as B2B renderProviderRow
// ─────────────────────────────────────────────────────────────────────────────

const IntRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  pill: { label: string; tone: PillTone };
  description: string;
  cta?: string;
  isOpen: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}> = ({ icon, label, pill, description, cta, isOpen, onToggle, children }) => (
  <div className="ds-akk-row">
    <div
      className="ds-akk-row-header"
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle()}
    >
      <div className="ds-akk-row-icon">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="ds-akk-row-title">{label}</p>
          <StatusPill label={pill.label} tone={pill.tone} />
        </div>
        <p className="ds-akk-row-desc">{description}</p>
      </div>
      {cta && (
        <span className="ds-akk-row-cta" aria-hidden>
          {cta}
          {isOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      )}
    </div>
    {isOpen && children && <div className="ds-akk-row-expand">{children}</div>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Section 1 — Personal API Keys
// ─────────────────────────────────────────────────────────────────────────────

const PersonalApiKeysSection: React.FC = () => {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genLabel, setGenLabel] = useState('');
  const [genOpen, setGenOpen] = useState(false);
  const [rawKey, setRawKey] = useState('');
  const [rawKeyPrefix, setRawKeyPrefix] = useState('');
  const [showRaw, setShowRaw] = useState(true);
  const [copiedRaw, setCopiedRaw] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [genErr, setGenErr] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setFetchErr(null);
    const { data, error } = await supabase
      .from('api_keys')
      .select('id, key_prefix, label, scopes, rate_limit_rpm, created_at, last_used_at, revoked_at')
      .is('revoked_at', null)
      .order('created_at', { ascending: false });
    setLoading(false);
    if (error) { setFetchErr(error.message); return; }
    setKeys((data ?? []) as ApiKeyRow[]);
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const handleGenerate = async () => {
    setGenerating(true);
    setGenErr(null);
    try {
      const { data, error } = await supabase.functions.invoke<GenerateResponse>(
        'api-key-generate',
        { body: { label: genLabel.trim() || undefined } },
      );
      if (error) throw new Error(error.message || 'Could not create key.');
      if (!data?.raw_key) throw new Error('Server did not return a key.');
      setRawKey(data.raw_key);
      setRawKeyPrefix(data.key_prefix);
      setGenOpen(false);
      setConfirmed(false);
      setCopiedRaw(false);
      setGenLabel('');
    } catch (e) {
      setGenErr(e instanceof Error ? e.message : 'Failed to generate key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevokeId(id);
    await supabase.from('api_keys').delete().eq('id', id);
    setRevokeId(null);
    await refresh();
  };

  const copyRaw = async () => {
    await navigator.clipboard.writeText(rawKey);
    setCopiedRaw(true);
    setTimeout(() => setCopiedRaw(false), 2000);
  };

  const pill: { label: string; tone: PillTone } = loading
    ? { label: 'Loading…', tone: 'off' }
    : { label: keys.length > 0 ? `${keys.length} active key${keys.length > 1 ? 's' : ''}` : 'No keys', tone: keys.length > 0 ? 'ok' : 'off' };

  return (
    <IntSection icon={<Shield className="w-3.5 h-3.5" />} title="Personal API Keys">
      <IntRow
        icon={<Shield className="w-4 h-4" />}
        label="GoGrabJob API Access"
        pill={pill}
        description="Personal access tokens tied to your account. Each key inherits your plan's scopes and rate limits."
        cta={isOpen ? 'Hide' : 'Manage'}
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      >
        <div className="ds-akk-callout-warn mt-3">
          <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <span>
            <strong className="font-semibold">Treat keys like passwords.</strong>{' '}
            We store only the SHA-256 hash — the raw value is shown once at generation time. If you lose a key, revoke it and generate a new one.
          </span>
        </div>

        {/* Raw key reveal after generation */}
        {rawKey && (
          <div className="ds-akk-callout-brand mt-3 space-y-3">
            <p className="t-meta font-semibold flex items-center gap-1.5 t-warn-label">
              <AlertTriangle className="w-3.5 h-3.5" aria-hidden /> Copy this key now — it will not be shown again.
            </p>
            <div className="relative">
              <code className="ds-akk-reveal-code select-all">
                {showRaw ? rawKey : '•'.repeat(rawKey.length)}
              </code>
              <button type="button" onClick={() => setShowRaw(v => !v)}
                className="ds-akk-btn-ghost absolute right-9 top-1/2 -translate-y-1/2 min-h-0 p-1">
                {showRaw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button type="button" onClick={copyRaw}
                className="ds-akk-btn-ghost absolute right-2 top-1/2 -translate-y-1/2 min-h-0 p-1">
                {copiedRaw ? <Check className="w-3.5 h-3.5" style={{ color: 'var(--ds-ok-text)' }} /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <span className="t-meta ds-akk-row-desc">
              Prefix: <code className="font-mono ds-akk-key-prefix">{rawKeyPrefix}…</code>
            </span>
            <label className="flex items-center gap-2 t-meta cursor-pointer select-none ds-akk-row-title">
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
                className="w-4 h-4 accent-[var(--ds-brand)]" />
              I've copied this key and stored it somewhere safe.
            </label>
            <button type="button" disabled={!confirmed} onClick={() => { setRawKey(''); void refresh(); }}
              className="ds-akk-btn ds-akk-btn-primary">
              Done
            </button>
          </div>
        )}

        {/* Error */}
        {(fetchErr ?? genErr) && (
          <div className="ds-akk-error mt-3">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden />
            {fetchErr ?? genErr}
          </div>
        )}

        {/* Generate form */}
        {genOpen ? (
          <div className="mt-3 space-y-3">
            <label className="flex flex-col gap-1.5 t-body">
              <span className="font-medium ds-akk-row-title">Key label</span>
              <input
                type="text"
                value={genLabel}
                maxLength={80}
                onChange={e => setGenLabel(e.target.value)}
                placeholder="e.g. Production server"
                autoFocus
                className="ds-akk-input"
              />
              <span className="t-meta ds-akk-row-desc">Optional. Up to 80 characters.</span>
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setGenOpen(false)} className="ds-akk-btn">
                Cancel
              </button>
              <button type="button" disabled={generating} onClick={() => void handleGenerate()}
                className="ds-akk-btn ds-akk-btn-primary">
                {generating && <LoadingSpinner size="xs" showText={false} />}
                Generate
              </button>
            </div>
          </div>
        ) : (
          <button type="button" onClick={() => setGenOpen(true)} className="ds-akk-btn mt-3">
            <Plus className="w-3.5 h-3.5" />
            Generate new API key
          </button>
        )}

        {/* Key list */}
        {loading ? (
          <div className="mt-4 flex items-center gap-2 t-body ds-akk-row-desc">
            <LoadingSpinner size="xs" showText={false} /> Loading keys…
          </div>
        ) : keys.length > 0 ? (
          <ul className="ds-akk-key-list list-none p-0 m-0">
            {keys.map(k => (
              <li key={k.id} className="ds-akk-key-item">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="ds-akk-row-title">{k.label || 'Untitled key'}</span>
                    <code className="ds-akk-key-prefix">{k.key_prefix}…</code>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 mt-0.5 t-meta ds-akk-row-desc">
                    <span>Created {shortDate(k.created_at)}</span>
                    <span>·</span>
                    <span>Last used {relativeTime(k.last_used_at)}</span>
                    <span>·</span>
                    <span>{k.rate_limit_rpm ?? 60} req/min</span>
                  </div>
                </div>
                <button type="button" disabled={revokeId === k.id}
                  onClick={() => void handleRevoke(k.id)}
                  className="ds-akk-btn ds-akk-btn-danger shrink-0 min-h-[32px] text-[12px]">
                  {revokeId === k.id ? <LoadingSpinner size="xs" showText={false} /> : <Trash2 className="w-3.5 h-3.5" />}
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        ) : !rawKey ? (
          <p className="mt-3 t-meta ds-akk-row-desc">
            No keys yet. Generate one above to start using the GoGrabJob API.
          </p>
        ) : null}
      </IntRow>
    </IntSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section 2 — AI & Intelligence
// ─────────────────────────────────────────────────────────────────────────────

const AI_MODELS = [
  {
    id: 'ollama',
    label: 'Ollama (Local LLM)',
    description: 'llama3.2:3b — runs at http://localhost:11434. Used for intent parsing, validation and agent reasoning.',
    icon: <Zap className="w-4 h-4" />,
    pill: { label: 'Local · Auto-detect', tone: 'info' as PillTone },
    configLabel: 'Endpoint',
    configPlaceholder: 'http://localhost:11434',
    storageKey: 'ggj_ollama_url',
    defaultVal: 'http://localhost:11434',
  },
  {
    id: 'groq',
    label: 'Groq (Cloud LLM)',
    description: 'Llama 3 via Groq API — used for resume optimisation and deep search. Fastest cloud inference.',
    icon: <Zap className="w-4 h-4" />,
    pill: { label: 'Cloud', tone: 'info' as PillTone },
    configLabel: 'API key',
    configPlaceholder: 'gsk_…',
    storageKey: 'ggj_groq_key',
    defaultVal: '',
  },
];

const AiModelsSection: React.FC = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const m of AI_MODELS) out[m.id] = localStorage.getItem(m.storageKey) ?? m.defaultVal;
    return out;
  });
  const [showVal, setShowVal] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<string | null>(null);

  const handleSave = (id: string, key: string) => {
    localStorage.setItem(key, values[id]);
    setSaved(id);
    setTimeout(() => setSaved(null), 1800);
  };

  return (
    <IntSection icon={<Zap className="w-3.5 h-3.5" />} title="AI & Intelligence">
      {AI_MODELS.map(m => (
        <IntRow
          key={m.id}
          icon={m.icon}
          label={m.label}
          pill={m.pill}
          description={m.description}
          cta={openId === m.id ? 'Close' : 'Configure'}
          isOpen={openId === m.id}
          onToggle={() => setOpenId(openId === m.id ? null : m.id)}
        >
          {saved === m.id && (
            <div className="ds-akk-saved mt-3">
              <Check className="w-3.5 h-3.5" aria-hidden /> Saved
            </div>
          )}
          <div className="mt-3 space-y-3">
            <label className="flex flex-col gap-1.5 t-body">
              <span className="font-medium ds-akk-row-title">{m.configLabel}</span>
              <div className="relative">
                <input
                  type={showVal[m.id] ? 'text' : (m.id === 'ollama' ? 'text' : 'password')}
                  value={values[m.id]}
                  onChange={e => setValues(v => ({ ...v, [m.id]: e.target.value }))}
                  placeholder={m.configPlaceholder}
                  className="ds-akk-input pr-9"
                />
                {m.id === 'groq' && (
                  <button type="button" onClick={() => setShowVal(v => ({ ...v, [m.id]: !v[m.id] }))}
                    className="ds-akk-btn-ghost absolute right-2 top-1/2 -translate-y-1/2 min-h-0 p-1">
                    {showVal[m.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </label>
            <button type="button" onClick={() => handleSave(m.id, m.storageKey)}
              className="ds-akk-btn ds-akk-btn-primary">
              <Save className="w-3.5 h-3.5" />
              Save {m.configLabel.toLowerCase()}
            </button>
          </div>
        </IntRow>
      ))}
    </IntSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section 3 — Job Channels (sourcing connectors)
// ─────────────────────────────────────────────────────────────────────────────

interface JobChannelDef {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  credKey?: string;   // maps to ExternalCred.key in localStorage
  internal?: boolean;
}

const JOB_CHANNELS: JobChannelDef[] = [
  {
    id: 'peerlist',
    label: 'Peerlist',
    description: "Built-in job board. Jobs are ingested automatically — no credentials needed.",
    icon: <Globe className="w-4 h-4" />,
    internal: true,
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    description: 'Job search via LinkedIn. Paste your li_at session cookie (from browser DevTools → Cookies).',
    icon: <Globe className="w-4 h-4" />,
    credKey: 'linkedin_li_at',
  },
  {
    id: 'naukri',
    label: 'Naukri',
    description: 'Job search via Naukri. Paste your nauk_at session token from browser DevTools.',
    icon: <Globe className="w-4 h-4" />,
    credKey: 'naukri_nauk_at',
  },
  {
    id: 'indeed',
    label: 'Indeed',
    description: 'Job search via Indeed. Paste your indeed-ssl cookie from browser DevTools.',
    icon: <Globe className="w-4 h-4" />,
    credKey: 'indeed_indeed_ssl',
  },
  {
    id: 'internshala',
    label: 'Internshala',
    description: 'Job and internship search via Internshala. Paste your session cookie.',
    icon: <Globe className="w-4 h-4" />,
    credKey: 'internshala_session',
  },
];

const JobChannelsSection: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const [creds, setCreds] = useState<ExternalCred[]>(() => loadExternalCreds());
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [show, setShow] = useState(false);
  const [savedId, setSavedId] = useState<string | null>(null);

  const findCred = (key: string) => creds.find(c => c.key === key);

  const handleConnect = (ch: JobChannelDef) => {
    if (!ch.credKey) return;
    const existing = findCred(ch.credKey);
    setDraft(existing?.value ?? '');
    setShow(false);
    setOpenId(ch.id);
  };

  const handleSave = (ch: JobChannelDef) => {
    if (!ch.credKey || !draft.trim()) return;
    const existing = findCred(ch.credKey);
    let next: ExternalCred[];
    if (existing) {
      next = creds.map(c => c.key === ch.credKey ? { ...c, value: draft.trim() } : c);
    } else {
      next = [...creds, { id: crypto.randomUUID(), key: ch.credKey, value: draft.trim(), description: ch.label }];
    }
    saveExternalCreds(next);
    setCreds(next);
    setSavedId(ch.id);
    setTimeout(() => setSavedId(null), 1800);
    setOpenId(null);
  };

  const handleDisconnect = (ch: JobChannelDef) => {
    if (!ch.credKey) return;
    const next = creds.filter(c => c.key !== ch.credKey);
    saveExternalCreds(next);
    setCreds(next);
  };

  return (
    <IntSection icon={<Globe className="w-3.5 h-3.5" />} title="Job Channels">
      {JOB_CHANNELS.map(ch => {
        const connected = ch.internal ? true : !!findCred(ch.credKey ?? '');
        const pill: { label: string; tone: PillTone } = ch.internal
          ? { label: 'Built-in · Active', tone: 'ok' }
          : connected ? { label: 'Connected', tone: 'ok' }
          : { label: 'Not configured', tone: 'off' };

        const ctaLabel = ch.internal
          ? undefined
          : connected ? 'Reconnect' : 'Connect';

        return (
          <IntRow
            key={ch.id}
            icon={ch.icon}
            label={ch.label}
            pill={pill}
            description={ch.description}
            cta={isAdmin ? ctaLabel : undefined}
            isOpen={openId === ch.id}
            onToggle={() => {
              if (!isAdmin || ch.internal) return;
              if (openId === ch.id) setOpenId(null);
              else handleConnect(ch);
            }}
          >
            {/* Credential paste form */}
            {savedId === ch.id && (
              <div className="ds-akk-saved mt-3">
                <Check className="w-3.5 h-3.5" aria-hidden /> Saved
              </div>
            )}
            <div className="mt-3 space-y-3">
              <label className="flex flex-col gap-1.5 t-body">
                <span className="font-medium ds-akk-row-title">Session cookie / token</span>
                <div className="relative">
                  <input
                    type={show ? 'text' : 'password'}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder="Paste value here…"
                    className="ds-akk-input ds-akk-input-mono pr-9"
                  />
                  <button type="button" onClick={() => setShow(v => !v)}
                    className="ds-akk-btn-ghost absolute right-2 top-1/2 -translate-y-1/2 min-h-0 p-1">
                    {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" disabled={!draft.trim()} onClick={() => handleSave(ch)}
                  className="ds-akk-btn ds-akk-btn-primary">
                  <Save className="w-3.5 h-3.5" />
                  Save & connect
                </button>
                {connected && (
                  <button type="button" onClick={() => handleDisconnect(ch)}
                    className="ds-akk-btn ds-akk-btn-danger">
                    Disconnect
                  </button>
                )}
                <button type="button" onClick={() => setOpenId(null)} className="ds-akk-btn-ghost min-w-[36px] p-2">
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </IntRow>
        );
      })}
    </IntSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Section 4 — Roadmap (dashed cards like B2B)
// ─────────────────────────────────────────────────────────────────────────────

const PLANNED = [
  'GitHub job board',
  'AngelList / Wellfound',
  'Wellfound API',
  'Glassdoor integrations',
  'Custom webhook push',
  'Outbound apply automation',
];

const RoadmapSection: React.FC = () => (
  <IntSection icon={<Globe className="w-3.5 h-3.5" />} title="Planned Integrations">
    <div className="p-4 grid gap-2 sm:grid-cols-2">
      {PLANNED.map(lbl => (
        <div key={lbl} className="ds-akk-roadmap-card">
          <p>{lbl}</p>
          <p>Planned</p>
        </div>
      ))}
    </div>
  </IntSection>
);

// ─────────────────────────────────────────────────────────────────────────────
// Section 5 — Encrypted Vault Keys (admin only)
// ─────────────────────────────────────────────────────────────────────────────

interface VaultKeyFieldProps {
  label:        string;
  placeholder:  string;
  value:        string;
  onChange:     (v: string) => void;
  show:         boolean;
  onToggleShow: () => void;
}

function VaultKeyField({ label, placeholder, value, onChange, show, onToggleShow }: VaultKeyFieldProps) {
  return (
    <div>
      <label className="block t-label mb-1.5 ds-akk-row-desc">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoComplete="off"
          className="ds-akk-input pr-10"
        />
        <button type="button" onClick={onToggleShow}
          className="ds-akk-btn-ghost absolute right-2.5 top-1/2 -translate-y-1/2 min-h-0 p-0.5"
          aria-label={show ? 'Hide key' : 'Show key'}>
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

const EncryptedVaultSection: React.FC<{ isAdmin?: boolean }> = ({ isAdmin }) => {
  const [showLinkedIn, setShowLinkedIn] = useState(false);
  const [showNaukri,   setShowNaukri]   = useState(false);
  const [linkedInKey,  setLinkedInKey]  = useState('');
  const [naukriKey,    setNaukriKey]    = useState('');

  if (!isAdmin) return null;

  return (
    <IntSection icon={<KeyRound className="w-3.5 h-3.5" />} title="Encrypted Platform Keys (Admin)">
      <div className="p-4 space-y-4">
        {/* Review notice */}
        <div className="ds-akk-callout-info">
          <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <span>
            <strong>REVIEW</strong> Supabase Vault (<code>vault.secrets</code>) must be enabled in the
            project settings before keys are written here. The save button is a UI stub — wire it to a{' '}
            <code>supabase.rpc('vault_upsert_secret', …)</code> call once Vault is active.
            <strong> Never store API keys in plain text.</strong>
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <VaultKeyField
            label="LinkedIn API key"
            placeholder="Paste LinkedIn API credential…"
            value={linkedInKey}
            onChange={setLinkedInKey}
            show={showLinkedIn}
            onToggleShow={() => setShowLinkedIn(v => !v)}
          />
          <VaultKeyField
            label="Naukri API key"
            placeholder="Paste Naukri API credential…"
            value={naukriKey}
            onChange={setNaukriKey}
            show={showNaukri}
            onToggleShow={() => setShowNaukri(v => !v)}
          />
        </div>

        <button type="button" disabled
          className="ds-akk-btn ds-akk-btn-primary mt-2 opacity-40 cursor-not-allowed"
          title="Enable Supabase Vault first — see notice above">
          Save encrypted (Vault not configured — [REVIEW])
        </button>
      </div>
    </IntSection>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Root export
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiKeysIntegrationsProps {
  isAdmin?: boolean;
}

const ApiKeysIntegrations: React.FC<ApiKeysIntegrationsProps> = ({ isAdmin = false }) => (
  <div className="ds-api-keys space-y-4">
    <PersonalApiKeysSection />
    <AiModelsSection />
    <JobChannelsSection isAdmin={isAdmin} />
    <RoadmapSection />
    {isAdmin && <EncryptedVaultSection isAdmin={isAdmin} />}
  </div>
);

export default ApiKeysIntegrations;
